import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { BehaviorSubject, Subject } from 'rxjs';
import { AboutYourBusinessFinancialRequired, ApplicationModel, AuthorizedPersonnelModel } from '../model';
import { IFormStore } from '../interface';
import { ApplicationHelperService } from '../service';
import { AuthorizedPersonnelOption } from '../type';
import { PERSONNEL_OTHER } from '../const/app.const';
import { isObject, merge, mergeWith } from 'lodash-es';
import { DeepPartial, parseAndFormatToStartOfLocalDate } from '@core/util';
import { DataAccessStore } from '../store';
import { DATEPICKER } from '@core/data-access';

type State = { state: 'idle' | 'invalid' } | { state: 'valid'; data: AboutYourBusinessFinancialRequired };

const initialState: State = { state: 'idle' };

const customizer = (objValue: any, srcValue: any) => {
  if (typeof srcValue === 'boolean') {
    return srcValue;
  }

  if (srcValue instanceof Date) {
    return parseAndFormatToStartOfLocalDate(srcValue, { format: DATEPICKER.PATTERN })?.format;
    // return parseLocalDateToISOZone(srcValue, SINGAPORE_ZONE);
  }

  // Ignore nullish srcValue
  if (!srcValue) {
    return objValue;
  }

  // If both values are arrays, merge them
  if (Array.isArray(objValue) && Array.isArray(srcValue)) {
    return Array.from(new Set(objValue.concat(srcValue)));
  }

  // If both values are objects, merge them
  if (isObject(objValue) && isObject(srcValue)) {
    return merge({}, objValue, srcValue);
  }

  // Otherwise, return srcValue (overwriting the existing value)
  return srcValue;
};

@Injectable({ providedIn: 'root' })
export class FinancialRequiredStore extends ComponentStore<State> implements IFormStore {
  private readonly dataAccessStore = inject(DataAccessStore);
  readonly setStateInvalid = this.updater(() => ({ state: 'invalid' }));
  readonly setDataValid = this.updater<AboutYourBusinessFinancialRequired>((_, data) => {
    const model = AboutYourBusinessFinancialRequired.fromJson(data);
    this.setPortalPersonnelOptionsMap({ personnels: model.keyPersonnels });
    return { state: 'valid', data: model };
  });
  readonly dataState$ = this.select(s => (s.state === 'valid' ? s : null));
  readonly submit$ = new Subject<void>();
  readonly portalPersonnelOptionsMap$ = new BehaviorSubject<Map<string, AuthorizedPersonnelOption>>(new Map());
  readonly currentPersonnel$ = new BehaviorSubject<AuthorizedPersonnelModel | null>(null);

  readonly updateCurrentPersonnel = this.updater<DeepPartial<AuthorizedPersonnelModel>>(
    (state, newPersonnel: DeepPartial<AuthorizedPersonnelModel>): State => {
      const currentPersonnel = this.currentPersonnel$.getValue() || AuthorizedPersonnelModel.createEmpty();
      const merged = mergeWith({}, currentPersonnel, newPersonnel, customizer);

      this.currentPersonnel$.next(merged);

      if (state.state === 'valid') {
        const model = state.data;
        const personnels = model.keyPersonnels.map<AuthorizedPersonnelModel>(p => {
          if (p.id === currentPersonnel.id) return merged;
          return p;
        });

        this.setPortalPersonnelOptionsMap({ personnels });

        return { state: 'valid', data: { ...model, keyPersonnels: personnels } };
      }

      return state;
    },
  );

  constructor() {
    super(initialState);
  }

  setFormDataBasedOnApplication(application: ApplicationModel): void {
    const model = AboutYourBusinessFinancialRequired.createEmpty();
    const personnels = application.keyPersonnels
      ?.map(v => {
        const personnel = AuthorizedPersonnelModel.fromJson(v);
        if (ApplicationHelperService.isPersonnelKeyman(personnel)) {
          personnel.preferredContactTime = application.preferredContactTime;
        }
        return personnel;
      })
      .filter(v => application.keyPersonnels.some(appPersonnel => appPersonnel.id === v.id));

    model.product = application.snapshot.product.title;
    model.amountRequired = application.sumFacilityAmount;
    model.tenure = application.sumTenorAmount;
    model.keyPersonnels = personnels || [];

    const jwt = this.dataAccessStore.jwt$.getValue();
    const currentPersonnel = personnels?.find(p => p?.id === jwt?.personnelId);
    this.currentPersonnel$.next(currentPersonnel || null);

    this.setDataValid(model);
  }

  setPortalPersonnelOptionsMap(args: { personnels: AuthorizedPersonnelModel[] }): void {
    const options = args.personnels.map<AuthorizedPersonnelOption>(p => {
      const uuid = p.uuid === 'PERSONNEL_OTHER' ? p.metadata?.uuid : p.uuid;
      return { label: p.name, value: uuid || null, disabled: false, personnel: p };
    });
    options.push({ label: 'Other', value: PERSONNEL_OTHER, disabled: false, personnel: null });
    const map = new Map(options.map(o => [o.value, o])); // unique by value

    this.portalPersonnelOptionsMap$.next(map);
  }
}
